/**
 * Utility functions for budget progress calculations
 */

export interface BudgetProgress {
  percentage: number;
  isOverBudget: boolean;
  status: 'success' | 'warning' | 'error';
  displayText: string;
}

/**
 * Calculate budget progress percentage and status
 * @param totalCost - Current total cost
 * @param budget - Maximum budget amount
 * @param warningThreshold - Percentage threshold for warning status (default: 90)
 * @returns BudgetProgress object with percentage, status, and display information
 */
export const calculateBudgetProgress = (
  totalCost: number,
  budget: number,
  warningThreshold: number = 90,
): BudgetProgress => {
  // Handle edge cases
  if (budget <= 0) {
    return {
      percentage: 0,
      isOverBudget: false,
      status: 'success',
      displayText: 'No budget set',
    };
  }

  if (totalCost < 0) {
    return {
      percentage: 0,
      isOverBudget: false,
      status: 'success',
      displayText: '0% of budget',
    };
  }

  const percentage = Math.round((totalCost / budget) * 100);
  const isOverBudget = totalCost > budget;

  // Determine status based on percentage and over-budget condition
  let status: 'success' | 'warning' | 'error';
  if (isOverBudget) {
    status = 'error';
  } else if (percentage >= warningThreshold) {
    status = 'warning';
  } else {
    status = 'success';
  }

  // Generate display text
  const displayText = isOverBudget
    ? `${percentage}% over budget`
    : `${percentage}% of budget`;

  return {
    percentage,
    isOverBudget,
    status,
    displayText,
  };
};

/**
 * Format budget progress for display
 * @param totalCost - Current total cost
 * @param budget - Maximum budget amount
 * @returns Formatted string showing progress
 */
export const formatBudgetProgress = (
  totalCost: number,
  budget: number,
): string => {
  const progress = calculateBudgetProgress(totalCost, budget);
  return progress.displayText;
};

/**
 * Get progress variant for UI components based on budget status
 * @param totalCost - Current total cost
 * @param budget - Maximum budget amount
 * @param warningThreshold - Percentage threshold for warning status (default: 90)
 * @returns Variant string for Progress component
 */
export const getBudgetProgressVariant = (
  totalCost: number,
  budget: number,
  warningThreshold: number = 90,
): 'primary' | 'secondary' | 'success' | 'warning' | 'error' => {
  const progress = calculateBudgetProgress(totalCost, budget, warningThreshold);
  return progress.status;
};
